import {
  PLAYER_COLORS,
  type ClientRoomState,
  type GuessMap,
  type Player,
  type RoundKind,
} from "@poppycock/shared";
import { AnimatePresence, motion } from "framer-motion";
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAutoSubmitOnTimeout } from "../hooks/useAutoSubmitOnTimeout";
import { AnswerBody } from "./DesignStage";
import { CaptionPrompt } from "./CaptionPrompt";
import { PhaseTimer } from "./PhaseTimer";
import { Button, Card, PhaseBadge, ProgressDots } from "./ui";

export function GuessingScreen({
  state,
  onSubmit,
}: {
  state: ClientRoomState;
  onSubmit: (guesses: GuessMap) => void;
}) {
  const round = state.round!;
  const youId = state.you.id;
  const submitted = round.youSubmitted;

  // Answers that aren't yours (we don't know authors yet — guess all)
  // Actually during guessing, authorId is hidden. Players assign chips to all cards.
  // Server skips self-author when scoring. Client can still assign all.
  const answers = round.answers;
  const otherPlayers = state.players.filter((p) => p.id !== youId);

  const [assignments, setAssignments] = useState<GuessMap>(
    () => ({ ...round.yourGuesses }),
  );
  const assignmentsRef = useRef(assignments);
  assignmentsRef.current = assignments;
  const [activeAnswer, setActiveAnswer] = useState<string | null>(
    answers[0]?.id ?? null,
  );

  const forceSubmit = useCallback(() => {
    onSubmit(assignmentsRef.current);
  }, [onSubmit]);

  const countdown = useAutoSubmitOnTimeout(
    state.phaseEndsAt,
    submitted,
    forceSubmit,
  );

  const usedAuthors = useMemo(
    () => new Set(Object.values(assignments)),
    [assignments],
  );

  const assignedCount = Object.keys(assignments).length;

  const assign = (answerId: string, playerId: string) => {
    setAssignments((prev) => {
      const next = { ...prev };
      // Remove this player from any other card
      for (const [aid, pid] of Object.entries(next)) {
        if (pid === playerId) delete next[aid];
      }
      // Toggle off if same
      if (prev[answerId] === playerId) {
        delete next[answerId];
      } else {
        next[answerId] = playerId;
      }
      return next;
    });
  };

  const playerById = (id: string): Player | undefined =>
    state.players.find((p) => p.id === id);

  if (submitted) {
    return (
      <div className="flex flex-1 flex-col">
        <Header round={round.number} kind={round.kind} />
        <Card className="flex flex-1 flex-col items-center justify-center py-12 text-center">
          <p className="mb-2 text-lg font-semibold">Guesses locked</p>
          <p className="mb-6 text-sm text-[var(--color-muted)]">
            Everyone else still poking chips around…
          </p>
          <ProgressDots
            submitted={round.submittedCount}
            total={round.totalPlayers}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col">
      <Header
        round={round.number}
        kind={round.kind}
        timer={
          <PhaseTimer
            label={countdown.label}
            urgency={countdown.urgency}
            active={countdown.active}
          />
        }
      />

      <Card className="mb-4">
        {round.kind === "caption" && round.imageId ? (
          <>
            <CaptionPrompt
              imageId={round.imageId}
              category={round.category}
              compact
            />
            <p className="mt-3 text-sm text-[var(--color-muted)]">
              Tap a caption, then tap a name. One chip per person.
            </p>
          </>
        ) : (
          <>
            <p className="mb-1 text-xs tracking-wide text-[var(--color-accent)] uppercase">
              Category
            </p>
            <p className="text-lg font-semibold tracking-tight">
              {round.category}
            </p>
            <p className="mt-2 text-sm text-[var(--color-muted)]">
              {round.kind === "design"
                ? "Tap a drawing, then tap a name chip. One chip per person."
                : "Tap a card, then tap a name chip. One chip per person."}
            </p>
          </>
        )}
      </Card>

      <ul className="mb-2 flex flex-1 flex-col gap-3 pb-2">
        <AnimatePresence>
          {answers.map((a, i) => {
            const assignedId = assignments[a.id];
            const assigned = assignedId ? playerById(assignedId) : null;
            const active = activeAnswer === a.id;
            return (
              <motion.li
                key={a.id}
                layout
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                <button
                  type="button"
                  onClick={() => setActiveAnswer(a.id)}
                  className={`glass w-full rounded-2xl p-4 text-left transition ${
                    active
                      ? "ring-2 ring-[var(--color-accent)]"
                      : "hover:bg-white/5"
                  }`}
                >
                  <div className="mb-3">
                    <AnswerBody answer={a} />
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] tracking-wide text-[var(--color-muted)] uppercase">
                      {active ? "Selected" : "Tap to assign"}
                    </span>
                    {assigned ? (
                      <span
                        className="rounded-full px-2.5 py-0.5 text-xs font-bold text-[#141414]"
                        style={{
                          background:
                            PLAYER_COLORS[
                              assigned.colorIndex % PLAYER_COLORS.length
                            ],
                        }}
                      >
                        {assigned.name}
                      </span>
                    ) : (
                      <span className="rounded-full border border-dashed border-white/20 px-2.5 py-0.5 text-xs text-[var(--color-muted)]">
                        ???
                      </span>
                    )}
                  </div>
                </button>
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ul>

      {/* Sticky mini palette + lock-in — rides along as you scroll the cards */}
      <div className="sticky bottom-0 z-40 -mx-1 mt-2 border-t border-white/10 bg-[#151416]/95 px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-xl">
        <div className="rounded-2xl border border-white/12 bg-[rgba(28,27,30,0.96)] p-3 shadow-[0_-8px_32px_rgba(0,0,0,0.45)]">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-[10px] font-semibold tracking-[0.14em] text-[var(--color-muted)] uppercase">
              {activeAnswer
                ? "Assign to selected card"
                : "Select a card, then a name"}
            </p>
            <p className="text-[10px] tabular-nums text-[var(--color-muted)]">
              {assignedCount}/{answers.length}
            </p>
          </div>
          <div className="mb-3 flex max-h-24 flex-wrap gap-2 overflow-y-auto">
            {otherPlayers.map((p) => {
              const used = usedAuthors.has(p.id);
              const color =
                PLAYER_COLORS[p.colorIndex % PLAYER_COLORS.length];
              return (
                <button
                  key={p.id}
                  type="button"
                  disabled={!activeAnswer}
                  onClick={() => activeAnswer && assign(activeAnswer, p.id)}
                  className={`rounded-full px-3 py-1.5 text-sm font-semibold transition ${
                    used
                      ? "opacity-35 line-through"
                      : "hover:scale-105 active:scale-95"
                  } ${!activeAnswer ? "cursor-not-allowed opacity-50" : ""}`}
                  style={{
                    background: color,
                    color: "#141414",
                  }}
                >
                  {p.name}
                </button>
              );
            })}
          </div>
          <Button className="w-full" onClick={() => onSubmit(assignments)}>
            Lock in guesses
          </Button>
        </div>
      </div>
    </div>
  );
}

function Header({
  round,
  kind,
  timer,
}: {
  round: number;
  kind?: RoundKind;
  timer?: ReactNode;
}) {
  const title =
    kind === "design"
      ? "Who drew that?"
      : kind === "caption"
        ? "Who captioned that?"
        : "Who wrote that?";

  return (
    <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="text-xs tracking-wide text-[var(--color-muted)] uppercase">
          Round {round}
        </p>
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {timer}
        <PhaseBadge phase="guessing" />
      </div>
    </header>
  );
}
